import React, { ReactElement } from 'react';
import {
  Box,
  Grid,
  Stack,
  Checkbox,
  FormControlLabel,
  Typography,
  Paper,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
} from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import { HeadTag } from 'shared/components';
import {
  AppFormikSelect,
  AppFormikTextArea,
  AppFormikTextField,
  AppPrimaryButton,
} from 'shared/components/AppComponents';
import { BusinessUsageInfoSchema } from 'validators/BusinessUsageInfoSchema';
import { PublicService } from 'services/PublicService';
import { Utils } from 'shared/helpers';

interface AISolutionFormValues {
  solutionName: string;
  solutionOwner: string;
  organizationUnit: string;
  organizationSubUnit: string;
  userCustomerName: string;
  purpose: string;
  uses: string;
  businessGoals: string;
  businessValue: string;
  riskTier: string;
  platformName: string;
  sourceType: string;
  publicSubmissionMail: string;
  isPublic: boolean;
  status: string;
}

const initialValues: AISolutionFormValues = {
  solutionName: '',
  solutionOwner: '',
  organizationUnit: '',
  organizationSubUnit: '',
  userCustomerName: '',
  purpose: '',
  uses: '',
  businessGoals: '',
  businessValue: '',
  riskTier: '',
  platformName: '',
  sourceType: '',
  publicSubmissionMail: '',
  isPublic: false,
  status: 'Pending',
};

const riskTiers = ['Low', 'Medium', 'High', 'Critical'];
const platforms = ['Web', 'Mobile', 'Desktop', 'API', 'Other'];

function BusinessUsageInformation(): ReactElement {
  const actionOnFormSubmit = (
    formValues: any,
    formik: FormikHelpers<any>
  ): void => {
    PublicService.submitAiSystem(formValues)
      .then((res) => {
        Utils.handleSuccessResponse(res);
      })
      .catch((err) => {
        Utils.handleErrorResponse(err);
      })
      .finally(() => {
        formik.setSubmitting(false);
        formik.resetForm();
      });
  };

  return (
    <>
      <Box className="ai-solution-management add-ai-solution">
        <HeadTag title="AI Solution Registration Form" />

        <Paper elevation={0} className="ai-solution-form-container">
          <Formik
            initialValues={initialValues}
            validationSchema={BusinessUsageInfoSchema}
            validateOnMount
            onSubmit={(formValues, formik) => {
              actionOnFormSubmit(formValues, formik);
            }}
          >
            {({
              handleSubmit,
              isSubmitting,
              values,
              setFieldValue,
              isValid,
              touched,
              errors,
            }) => (
              <Grid container spacing={3} className="add-ai-solution-wrapper">
                {/* Business & Usage Information Section */}
                <Grid item xs={12}>
                  <Paper variant="outlined" className="p-4">
                    <Typography variant="h3" className="section-title">
                      Business & Usage Information
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <AppFormikTextField
                          name="solutionName"
                          placeholder="Enter solution name"
                          label="Solution Name *"
                          outerLabel
                          required
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <AppFormikTextField
                          name="solutionOwner"
                          placeholder="Enter solution owner"
                          label="Solution Owner *"
                          outerLabel
                          required
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <AppFormikTextField
                          name="organizationUnit"
                          placeholder="Enter organisation unit"
                          label="Organisation Unit"
                          outerLabel
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <AppFormikTextField
                          name="organizationSubUnit"
                          placeholder="Enter organisation sub unit"
                          label="Organisation Sub Unit"
                          outerLabel
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <AppFormikTextField
                          name="userCustomerName"
                          placeholder="Enter user or customer information"
                          label="User / Customer"
                          outerLabel
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <AppFormikTextArea
                          name="purpose"
                          placeholder="Describe the purpose of this AI solution"
                          label="Purpose *"
                          rows={3}
                          outerLabel
                          required
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <AppFormikTextArea
                          name="uses"
                          placeholder="Describe how this AI solution will be used"
                          label="Uses *"
                          rows={3}
                          outerLabel
                          required
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <AppFormikTextArea
                          name="businessGoals"
                          placeholder="Describe business goals"
                          label="Business Goals"
                          rows={2}
                          outerLabel
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <AppFormikTextArea
                          name="businessValue"
                          placeholder="Describe business value"
                          label="Business Value"
                          rows={2}
                          outerLabel
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <AppFormikSelect
                          name="riskTier"
                          className="risk-tier-select"
                          labelId="risk-tier-label"
                          testid="risk-tier-dropdown"
                          MenuProps={{ className: 'menu-fix-selection' }}
                          outerLabel
                          placeholder="Select risk tier"
                          notEmphasized
                          notSelectablePlaceholder
                          label="Risk Tier *"
                          items={riskTiers}
                          required
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <AppFormikSelect
                          name="platformName"
                          className="platform-select"
                          labelId="platform-label"
                          testid="platform-dropdown"
                          MenuProps={{ className: 'menu-fix-selection' }}
                          outerLabel
                          placeholder="Select platform"
                          notEmphasized
                          notSelectablePlaceholder
                          label="Platform *"
                          items={platforms}
                          required
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Technical Details Section */}
                <Grid item xs={12}>
                  <Paper variant="outlined" className="p-4">
                    <Typography variant="h3" className="section-title">
                      Technical Details
                    </Typography>

                    <FormControl component="fieldset">
                      <FormLabel component="legend" className="form-label">
                        AI Source Type *
                      </FormLabel>
                      <RadioGroup
                        name="sourceType"
                        value={values.sourceType}
                        onChange={(e) =>
                          setFieldValue('sourceType', e.target.value)
                        }
                      >
                        <FormControlLabel
                          value="internal"
                          control={<Radio />}
                          label="Internal AI (Built in-house by your organisation)"
                        />
                        <FormControlLabel
                          value="thirdParty"
                          control={<Radio />}
                          label="Third Party AI (Provided by an external vendor)"
                        />
                      </RadioGroup>
                      {touched.sourceType && errors.sourceType && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 1, display: 'block' }}
                        >
                          {errors.sourceType}
                        </Typography>
                      )}
                    </FormControl>
                  </Paper>
                </Grid>

                {/* Contact Information Section */}
                <Grid item xs={12}>
                  <Paper variant="outlined" className="p-4">
                    <Typography variant="h3" className="section-title">
                      Contact Information (Optional)
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <AppFormikTextField
                          name="publicSubmissionMail"
                          placeholder="your.email@example.com"
                          label="Your Email"
                          type="email"
                          outerLabel
                          helperText="Optional: Provide your email if you'd like to be contacted about this submission."
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Consent and Submit Section */}
                <Grid item xs={12}>
                  <Paper variant="outlined" className="p-4">
                    <Grid item xs={12} style={{ marginBottom: '20px' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.isPublic}
                            onChange={(e) =>
                              setFieldValue('isPublic', e.target.checked)
                            }
                            name="isPublic"
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2" className="consent-text">
                            I understand that this information will be reviewed
                            by Tempo Pty Ltd and may be added to their AI
                            inventory system. I consent to the processing of
                            this data for the purpose of AI governance and
                            compliance.
                          </Typography>
                        }
                      />
                      {touched.isPublic && errors.isPublic && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 1, display: 'block' }}
                        >
                          {errors.isPublic}
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <Stack direction="row" spacing={2}>
                        <AppPrimaryButton
                          fullWidth
                          isLoading={isSubmitting}
                          disabled={isSubmitting || !isValid}
                          title="Submit AI Solution"
                          type="submit"
                          onClick={handleSubmit}
                        />
                      </Stack>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </Formik>
        </Paper>
      </Box>
    </>
  );
}

export default BusinessUsageInformation;
